import { api } from "../config/variables";
import { readCookie } from "../utils/functions";
import { isMobile } from "../utils/mobileDetection";

import SERVICE_NAMES from "./constants/serviceConstants";
import moEngageQueue, { EVENT_PRIORITY } from "./moengage/MoEngageQueue";
import { shouldSendToService } from "./registry/eventRegistry";

/**
 * Analytics Service Manager
 * Provides unified interface for all analytics services
 */
class AnalyticsManager {
	constructor() {
		this.services = {
			postHog: null,
			moEngage: null,
			gtm: null,
		};
		this.isInitialized = false;
	}

	/**
	 * Initialize all analytics services
	 */
	async initialize() {
		try {
			const importPromises = [];

			if (api.postHog?.isEnabled) {
				importPromises.push(
					import("./services/postHog").then(({ default: postHogService }) => {
						this.services.postHog = postHogService;
						postHogService.initialize();
						return postHogService;
					})
				);
			}

			if (api.moEngage?.isEnabled) {
				importPromises.push(
					import("./services/moEngage")
						.then(async ({ default: moEngageService }) => {
							this.services.moEngage = moEngageService;
							// Wire the queue before initialising the SDK so any events
							// fired during init are captured and flushed once ready.
							moEngageQueue.init(moEngageService);
							await moEngageService.initialize();
							return moEngageService;
						})
						.catch((error) => {
							console.error("Failed to load MoEngage service:", error);
							// The service failed to load so the queue will never get a
							// service reference and can never flush. Clear persisted events
							// to prevent unbounded localStorage growth across reloads.
							moEngageQueue.clearQueue();
							return null;
						})
				);
			}

			if (api.gtm?.isEnabled) {
				importPromises.push(
					import("./services/gtm")
						.then(({ default: gtmService }) => {
							this.services.gtm = gtmService;
							gtmService.initialize();
							return gtmService;
						})
						.catch((error) => {
							console.error("Failed to load GTM service:", error);
							return null;
						})
				);
			}

			await Promise.all(importPromises);
			this.isInitialized = true;
			// eslint-disable-next-line no-console
			console.log("Analytics Manager initialized successfully");
		} catch (error) {
			console.error("Failed to initialize Analytics Manager:", error);
		}
	}

	/**
	 * Track event across all configured analytics providers.
	 *
	 * MoEngage is routed through the event queue so events fired before the
	 * SDK finishes loading are buffered and flushed automatically — the caller
	 * does not need to wait for initialization.
	 *
	 * PostHog and GTM still require full initialization before they will fire.
	 *
	 * @param {string} eventName - Name of the event
	 * @param {Object} properties - Event properties
	 * @param {Object} options - Additional options passed to each SDK
	 * @param {number} [priority] - Queue priority for MoEngage (EVENT_PRIORITY.HIGH | NORMAL | LOW)
	 */
	trackEvent(
		eventName,
		properties = {},
		options = {},
		priority = EVENT_PRIORITY.NORMAL
	) {
		// Guard: nothing to do without a valid event name.
		if (!eventName || typeof eventName !== "string") {
			return;
		}

		const clientCookie = readCookie("client", true);
		const env = api.currentEnv;
		const isStaff = Boolean(clientCookie?.is_staff);
		const commonProperties = {
			source: isMobile() ? "mweb" : "web",
			email: clientCookie?.email || "",
			company_id: clientCookie?.client_poc?.client?.sr_id || "",
			phone_number: clientCookie?.contact_no || "",
		};

		// Shared env eligibility — must be staging/production (or dev with debug on).
		// Returns false for unknown envs (undefined, "test", "local", etc.) so events
		// never fire in environments that weren't explicitly configured.
		const isEnabledForEnv = (debugFlag) => {
			if (env === "dev") {
				return Boolean(debugFlag);
			}
			if (env === "staging" || env === "production") {
				return !isStaff;
			}
			return false;
		};

		// ── MoEngage — queue-backed, works before and after initialization ──────
		// isReady() is intentionally NOT checked here; the queue buffers the event
		// and flushes it once the SDK becomes available.
		const moEngageEnabled =
			api.moEngage?.isEnabled &&
			shouldSendToService(eventName, SERVICE_NAMES.MOENGAGE) &&
			isEnabledForEnv(api.moEngage?.debug);

		if (moEngageEnabled) {
			const moEngageProperties = {
				...commonProperties,
				bu_name: "Cargo",
				is_first: "TRUE",
				...properties,
			};
			moEngageQueue.track(eventName, moEngageProperties, options, priority);
		}

		// ── PostHog & GTM — require full initialization ──────────────────────────
		if (!this.isInitialized) {
			return;
		}

		const isServiceReady = (serviceKey, serviceName, debugFlag) => {
			const service = this.services[serviceKey];
			if (
				!service ||
				!service.isReady() ||
				!shouldSendToService(eventName, serviceName)
			) {
				return false;
			}
			if (env === "dev") {
				return Boolean(debugFlag);
			}
			if (env === "staging" || env === "production") {
				return !isStaff;
			}
			return false;
		};

		if (isServiceReady("postHog", SERVICE_NAMES.POSTHOG, api.postHog?.debug)) {
			this.services.postHog.trackEvent(
				eventName,
				{ ...commonProperties, ...properties },
				options
			);
		}

		if (isServiceReady("gtm", SERVICE_NAMES.GOOGLEANALYTICS, api.gtm?.debug)) {
			this.services.gtm.trackEvent(
				eventName,
				{ ...commonProperties, bu_name: "Cargo", ...properties },
				options
			);
		}
	}

	/**
	 * Get service status
	 * @returns {Object} Status of all services
	 */
	getServiceStatus() {
		return {
			postHog: this.services.postHog ? this.services.postHog.isReady() : false,
			moEngage: this.services.moEngage
				? this.services.moEngage.isReady()
				: false,
			gtm: this.services.gtm ? this.services.gtm.isReady() : false,
		};
	}

	/**
	 * Get detailed service loading status for debugging
	 * @returns {Object} Detailed status of all services
	 */
	getDetailedServiceStatus() {
		return {
			isInitialized: this.isInitialized,
			services: {
				postHog: {
					loaded: Boolean(this.services.postHog),
					ready: this.services.postHog
						? this.services.postHog.isReady()
						: false,
					service: this.services.postHog,
				},
				moEngage: {
					loaded: Boolean(this.services.moEngage),
					ready: this.services.moEngage
						? this.services.moEngage.isReady()
						: false,
					service: this.services.moEngage,
				},
				gtm: {
					loaded: Boolean(this.services.gtm),
					ready: this.services.gtm ? this.services.gtm.isReady() : false,
					service: this.services.gtm,
				},
			},
		};
	}
}

const analyticsManager = new AnalyticsManager();
export default analyticsManager;
